import cx from 'classnames'
import { useProperties } from 'hooks/queries/companies/properties/useProperties'
import { Spinner } from 'common/Spinner/Spinner'
import Styles from './Properties.module.scss'
import { Property, PropertyLabelMap, PropertyType } from '../../../api/clients/PropertyClient'
import { ReactComponent as EditIcon } from 'assets/icons/edit16.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus16.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash16.svg'
import PropertyIcon from '../../../common/PropertyItem/PropertyIcon'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { orderBy } from 'lodash'
import { useMemo, useState } from 'react'
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrowDown16.svg'
import { ReactComponent as ArrowUpIcon } from '../../../assets/icons/arrowUp16.svg'

type SortCols = 'name' | 'type' | 'default_for_articles'
type SortDirections = 'asc' | 'desc'

interface Props {
  onEditButtonClick: (property: Property) => void
  onToggleDefaultClick?: (property: Property) => Promise<void> | void
  onRemoveDefaultClick?: (property: Property) => Promise<void> | void
  onDeleteProperty: (property: Property) => Promise<void> | void
  setFormProperty: (property: Partial<Property> | undefined) => void
  setType: (type: PropertyType) => void
}

export function PropertiesTable({
  onEditButtonClick,
  onToggleDefaultClick,
  onDeleteProperty,
  setFormProperty,
  setType,
}: Props) {
  const propertiesQuery = useProperties({
    non_default: true,
    unscoped: true,
  })

  const [sort, setSort] = useState<[SortCols, SortDirections]>(['name', 'asc'])
  const [sortCol, sortDirection] = sort

  const sortedData = useMemo(() => {
    if (!propertiesQuery.data?.data?.properties?.length) return []

    return orderBy(propertiesQuery.data?.data.properties, [sortCol], [sortDirection])
  }, [propertiesQuery.data?.data.properties, sortCol, sortDirection])

  if (propertiesQuery.isLoading) {
    return <Spinner />
  }

  const sortHandler = (col: SortCols) => {
    return () => {
      setSort(([oldCol, direction]) => {
        if (col !== oldCol) return [col, 'asc']

        return direction === 'asc' ? [col, 'desc'] : [col, 'asc']
      })
    }
  }

  const sortIcon = (col: SortCols) => {
    if (sortCol !== col) return null

    if (sortDirection === 'asc') return <ArrowUpIcon className={Styles.sortIcon} />

    return <ArrowDownIcon className={Styles.sortIcon} />
  }

  return (
    <>
      <table className={Styles.table}>
        <thead>
          <tr className={Styles.tableHeaderRow}>
            <th align={'left'} className={cx({ [Styles.selected]: sortCol === 'name' })}>
              <div onClick={sortHandler('name')}>
                Property name
                {sortIcon('name')}
              </div>
            </th>
            <th align={'left'} className={cx({ [Styles.selected]: sortCol === 'type' })}>
              <div onClick={sortHandler('type')}>
                Type
                {sortIcon('type')}
              </div>
            </th>
            <th align={'center'} className={cx({ [Styles.selected]: sortCol === 'default_for_articles' })}>
              <div onClick={sortHandler('default_for_articles')}>
                Show by default
                {sortIcon('default_for_articles')}
              </div>
            </th>
            <th className={Styles.actionColumn}></th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((property: Property) => {
            return (
              <tr key={property.id} className={Styles.propertyRow}>
                <td>{property.name}</td>
                <td>
                  <div>
                    <PropertyIcon type={property.type} />
                    {PropertyLabelMap[property.type]}
                  </div>
                </td>
                <td align="center">
                  <Checkbox
                    aria-label="Edit Checkbox Property"
                    isSelected={property.default_for_articles}
                    onChange={() => onToggleDefaultClick && onToggleDefaultClick(property)}
                    size="md"
                    variant="grey"
                    className={Styles.containedClick}
                  />
                </td>
                <td align="right" className={Styles.actionColumn}>
                  <Button
                    variant="tertiary"
                    frontIcon={EditIcon}
                    size="md"
                    onPress={() => onEditButtonClick(property)}
                    className={Styles.actionIcon}
                  ></Button>
                  <Tooltip label="Delete" placement="top">
                    <Button
                      variant="tertiary"
                      frontIcon={TrashIcon}
                      size="md"
                      onPress={() => {
                        onDeleteProperty(property)
                      }}
                      className={Styles.actionIcon}
                    ></Button>
                  </Tooltip>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <table className={Styles.table}>
        <tbody>
          <tr
            key={'newProperty'}
            onClick={() => {
              setFormProperty({})
              setType(PropertyType.SingleSelect)
            }}
            className={Styles.newButton}
          >
            <PlusIcon /> New
          </tr>
        </tbody>
      </table>
    </>
  )
}
